/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {useMemo} from 'react';
import Chart from 'react-apexcharts';
import {SearchVolumeData} from '../../util/interfaces';
import {useTranslation} from 'react-i18next';

interface Props {
    searchVolumes: Array<SearchVolumeData>;
    width?: number | string;
    height?: number | string;
}

export default function SearchVolumePieChart(props: Props) {
    const {t} = useTranslation();
    const {searchVolumes, width, height} = props;
    const countryTotals = useMemo(() => {
        const countryTotals: Record<string, number> = {};
        for (const searchVolume of searchVolumes) {
            for (const [countryCode, total] of Object.entries(searchVolume.country_totals)) {
                if (!Object.hasOwn(countryTotals, countryCode)) countryTotals[countryCode] = 0;
                countryTotals[countryCode] += total;
            }
        }
        return countryTotals;
    }, [searchVolumes]);
    const [series, options] = useMemo(() => {
        const series: Array<number> = [];
        const labels: Array<string> = [];
        const colors: Array<string> = [];
        for (const [countryCode, total] of Object.entries(countryTotals)) {
            series.push(total);
            labels.push(getCountryName(countryCode));
            if (countryCode === 'NL') colors.push('#ffa501');
            else if (countryCode === 'BE') colors.push('#ed2939');
        }
        const options = {
            labels,
            colors,
            chart: {
                toolbar: {show: false}
            }
        };
        return [series, options];
    }, [countryTotals]);

    function getCountryName(countryCode: string) {
        if (countryCode === 'NL') return t('searchVolume.country.nl', 'Nederland');
        else if (countryCode === 'BE') return t('searchVolume.country.be', 'België');
        return '';
    }

    return (
        <div className='search-volume-pie-chart'>
            <Chart options={options} series={series} type='pie' width={width} height={height} />
        </div>
    );
}
